/**
 * Geodata inventory for Bunny sync/purge extract.
 * Leaf module: do not import constants.ts (icons / env graphs).
 *
 * CDN_ASSET_S3_OBJECTS are bucket/key paths only — never https:// URLs.
 * Trailing slash = prefix copy (sedero COGs, calwave HTML plots).
 */

export const NAUTILUS_STORAGE_DOMAIN = 'https://s3-west.nrp-nautilus.io'
/** Site pull zone (shell + geodata). Must match vars.APP_URL (no trailing slash). */
export const CDN_DOMAIN = 'https://cosmos-adapts.coastalresilience.ucsc.edu'

export const GEODATALAKE_BUCKET = 'vizlab-geodatalake-exports'
export const SPATIALOPS_PREFIX = 'cosmos-adapts'
export const VIDEO_BUCKET = 'nbs-adapts-videos'

const cosmos = `${GEODATALAKE_BUCKET}/${SPATIALOPS_PREFIX}`

/**
 * Stable site-shell paths (not hashed `/assets/*`).
 * Deploy workflow purges these after Bunny upload.
 * `/` is the directory URL Bunny serves as index.html (separate cache key).
 */
export const cacheEntrypoints = [
  '/',
  'index.html',
  'manifest.json',
  'favicon.svg',
  'robots.txt',
  'UCSC-CCCR-Cmark.svg',
  'style.json',
  'light_basemap_style.json',
  'tiles.json',
  'light_basemap_tiles.json',
  'sprite.json',
  'images/*',
  'fonts/*',
] as const

/** Keys match PMTileSources values in constants.ts. */
export const PMTILE_S3_OBJECTS = {
  floodplains_santacruz: `${cosmos}/vector/floodplains_09-29-26.pmtiles`,
  floodplains_ventura: `${cosmos}/vector/floodplains_ventura_07-14-26.pmtiles`,
  floodplains_halfmoonbay: `${cosmos}/vector/floodplains_halfmoonbay_07-14-26.pmtiles`,
  building_damages_santacruz: `${cosmos}/vector/building_damages_07-14-26.pmtiles`,
  building_damages_ventura: `${cosmos}/vector/building_damages_ventura_07-14-26.pmtiles`,
  building_damages_halfmoonbay: `${cosmos}/vector/building_damages_halfmoonbay_07-14-26.pmtiles`,
  road_flooding_santacruz: `${cosmos}/vector/road_flooding_07-16-26.pmtiles`,
  road_flooding_ventura: `${cosmos}/vector/road_flooding_ventura_07-16-26.pmtiles`,
  road_flooding_halfmoonbay: `${cosmos}/vector/road_flooding_halfmoonbay_07-16-26.pmtiles`,
  adaptation_santacruz: `${cosmos}/vector/adaptation_07-27-26.pmtiles`,
  adaptation_ventura: `${cosmos}/vector/adaptation_ventura_07-27-26.pmtiles`,
  adaptation_halfmoonbay: `${cosmos}/vector/adaptation_halfmoonbay_07-14-26.pmtiles`,
  waves: `${cosmos}/vector/waves-2.pmtiles`,
  bathymetry: `${cosmos}/raster/bathymetry.pmtiles`,
} as const

/** Dated publish suffix per site. Publishing is add-only; bump a site to adopt a new build. */
export const FLOOD_CONTOUR_DATE = {
  santacruz: '09-29-26',
  ventura: '07-20-26',
  halfmoonbay: '07-20-26',
} as const

/** Keys match FloodContourSources values in constants.ts. */
export const FLOOD_CONTOUR_S3_OBJECTS = {
  flood_contours_santacruz: `${cosmos}/vector/flood_contours_${FLOOD_CONTOUR_DATE.santacruz}.pmtiles`,
  flood_contours_ventura: `${cosmos}/vector/flood_contours_ventura_${FLOOD_CONTOUR_DATE.ventura}.pmtiles`,
  flood_contours_halfmoonbay: `${cosmos}/vector/flood_contours_halfmoonbay_${FLOOD_CONTOUR_DATE.halfmoonbay}.pmtiles`,
} as const

export const BATHYMETRY_COG_OBJECT =
  `${cosmos}/raster/2024-08-CaliWaves-ComposedBathymetry_web_cog.tif` as const

/** Dated publish prefix per site. Publishing is add-only; bump a site to adopt a new build. */
export const SEDERO_DATE_SUFFIX = {
  santacruz: '09-02-26',
  ventura: '09-18-26',
} as const
export const SEDERO_S3_PREFIXES = Object.values(SEDERO_DATE_SUFFIX).map(
  (date) => `${cosmos}/raster/sedero/${date}/`
)

export const CALWAVE_PLOTS_S3_PREFIX = `${GEODATALAKE_BUCKET}/calwave_plots/`

/**
 * S3 object paths (bucket/key) for geodata on the app Storage zone.
 * Version data by path — upload new keys only; never overwrite existing objects.
 * HLS omitted: nothing in the app still points at nbs-adapts-videos.
 */
export const CDN_ASSET_S3_OBJECTS = [
  ...Object.values(PMTILE_S3_OBJECTS),
  ...Object.values(FLOOD_CONTOUR_S3_OBJECTS),
  BATHYMETRY_COG_OBJECT,
  ...SEDERO_S3_PREFIXES,
  CALWAVE_PLOTS_S3_PREFIX,
] as const

function cosmosObject(assetKey: string): string {
  return `${GEODATALAKE_BUCKET}/${SPATIALOPS_PREFIX}/${assetKey}`
}

// flip to true to hit the pull zone from a local Vite dev server.
const forceCDN = false
const isOfflineBuild = import.meta.env?.VITE_OFFLINE_BUILD
const originHost =
  forceCDN || import.meta.env?.PROD
    ? import.meta.env.VITE_CDN_DOMAIN?.trim().replace(/\/$/, '') || CDN_DOMAIN
    : NAUTILUS_STORAGE_DOMAIN

/** HTTP URL for a bucket/key path. Trailing slash stripped. */
export function originUrl(s3ObjectPath: string): string {
  return `${originHost}/${s3ObjectPath.replace(/\/+$/, '')}`
}

export function pmTilesUrl(s3ObjectPath: string): string {
  if (isOfflineBuild) {
    const prefix = `${GEODATALAKE_BUCKET}/${SPATIALOPS_PREFIX}/`
    const key = s3ObjectPath.startsWith(prefix) ? s3ObjectPath.slice(prefix.length) : s3ObjectPath
    return `/${key}`
  }
  return originUrl(s3ObjectPath)
}

export function cogUrl(s3ObjectPath: string): string {
  return originUrl(s3ObjectPath)
}

/** Relative to cosmos-adapts/ (sedero web_key, etc.). */
export function cdnUrl(assetKey: string): string {
  if (assetKey.startsWith('https://')) return assetKey
  if (isOfflineBuild) return `/${assetKey}`
  return originUrl(cosmosObject(assetKey))
}

export function videoFolder(videoFile: string): string {
  if (isOfflineBuild) return `/videos/${videoFile}`
  return originUrl(`${VIDEO_BUCKET}/${videoFile}`)
}

export function videoUrl(videoFile: string): string {
  return `${videoFolder(videoFile)}/${videoFile}.m3u8`
}
